import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { useSession } from '../session/SessionProvider';

/**
 * usePAQuery — one read, four states, and the ONE place a mid-session 401 is handled.
 *
 * # Why the 401 lives here and not in `api/client.ts`
 *
 * `client.ts` deliberately installs no global «401 → redirect»: a 401 is a NORMAL answer
 * on this surface (the idle TTL slid past) and the SESSION PROVIDER is what turns it into
 * the login screen. A redirect inside the client would fight that state machine and would
 * also fire during the bootstrap, where 401 means «first visit».
 *
 * But a 401 arriving on a MONEY read is a different event from a 401 on the bootstrap: the
 * subscriber is looking at a page, holding a session that has just expired or been revoked
 * (a password change elsewhere, a staff archive of the card, a restart past the TTL). If
 * nothing interprets it he is shown an error panel that says the portal is broken and
 * invites him to retry for ever, when the answer is «log in again».
 *
 * So this hook asks the session to RE-READ itself. `refresh()` calls `/pa/auth/me`, gets
 * the same 401, and moves the app to `anonymous` — which renders the login screen through
 * the one code path that already exists. The query stays in `loading` meanwhile, so no
 * broken screen flashes on the way out.
 *
 * ⚠ It must not set an error state on 401. That was the first shape, and it produced
 * exactly the panel described above for a quarter of a second before the login screen
 * replaced it — a flash of «личный кабинет недоступен» that a subscriber remembers and
 * telephones about.
 *
 * # Why `load` is not in the dependency list
 *
 * Every caller writes it as an inline closure over its own filter state, so it is a new
 * function on every render and a dependency on it would loop for ever. `deps` is what the
 * caller declares instead — the values the request is actually built from — and it is what
 * a reload keys on. This is the same trade `apps/fsm` made and it is stated here so the
 * next reader does not "fix" it into an infinite fetch.
 */

export type PAQuery<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: unknown };

export interface PAQueryResult<T> {
  query: PAQuery<T>;
  /** Re-runs the read — the «Повторить» button, and what a filter change triggers. */
  reload: () => void;
}

export function usePAQuery<T>(load: () => Promise<T>, deps: readonly unknown[]): PAQueryResult<T> {
  const { refresh } = useSession();
  const [state, setState] = useState<PAQuery<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const data = await load();
        if (!cancelled) setState({ status: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // The session is gone. Let the provider discover it and render the login
          // screen; stay in `loading` so nothing broken is shown on the way there.
          void refresh();
          return;
        }
        setState({ status: 'error', error: err });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt, refresh]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { query: state, reload };
}
